import { LightningElement, api, wire } from "lwc";
import { getClient } from "@devforce/trailhead-client-api";
import { localizeDate } from "@devforce/trailhead-client-locale";

import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";

import { attrToBool } from "common/helpers";

import { USER_ORGS_ROUTE } from "../orgPicker/constants";
import * as locales from "./locales";
import { Org } from "../orgPicker/types";

export default class extends LightningElement {
  @api unitApiName: string | null = null;
  @api readOnly: boolean = false;
  @api selected: boolean = false;
  @api org: Org | null = null;

  @wire(LabelsAdapter, { locales, options: "$labelOptions" })
  private labels: LabelsAdapterValue = null!;

  private get labelOptions() {
    const createdOnDate = this.org?.created_at
      ? localizeDate(this.org.created_at)
      : "";
    const lastUsedDate = this.org?.last_used_for
      ? localizeDate(this.org.last_used_for.used_on)
      : "";

    return {
      createdOn: {
        date: createdOnDate,
      },
      lastUsed: {
        date: lastUsedDate,
      },
      usedForThis: {
        date: lastUsedDate,
      },
      selectOrg: {
        orgName: this.org?.name || "",
      },
    };
  }

  private isDeleting: boolean = false;

  private fromTrailhead = getClient();

  private get className() {
    if (!this.org) {
      return "option";
    }

    return [
      "option",
      this.selectable ? "option--selectable" : false,
      !this.readOnly && this.org.selected ? "option--active" : false,
    ]
      .filter(Boolean)
      .join(" ");
  }

  private get isSelected() {
    return attrToBool(this.selected);
  }

  private get statusLabel() {
    if (!this.org) return undefined;

    if (this.org.signup_request_status === "pending") {
      return this.labels.creating;
    }

    if (this.org.last_used_for) {
      if (this.org.last_used_for.unit_api_name === this.unitApiName) {
        return this.labels.usedForThis;
      }

      return this.labels.lastUsed;
    }

    if (this.org.created_at) {
      return this.labels.createdOn;
    }

    return undefined;
  }

  private get isLoading() {
    return !!this.org && this.org.signup_request_status === "pending";
  }

  private get selectable() {
    return (
      !this.readOnly && !!this.org && !this.org.reconnect && !this.org.expired
    );
  }

  private get showActions() {
    return (
      !this.readOnly && !!this.org && (this.org.reconnect || this.org.expired)
    );
  }

  private handleSelect() {
    if (this.selectable) {
      this.dispatchEvent(new CustomEvent("select", { detail: this.org!.id }));
    }
  }

  private async handleDelete() {
    this.isDeleting = true;
    try {
      await this.fromTrailhead.delete(`${USER_ORGS_ROUTE}/${this.org!.id}`);
      this.dispatchEvent(new CustomEvent("delete", { detail: this.org!.id }));
    } catch (error) {
      this.dispatchEvent(new CustomEvent("error", { detail: error }));
    } finally {
      this.isDeleting = false;
    }
  }
}
